import { useMemo, useState } from "react";
import NewContact from "../components/modals/NewContact";
import ImportContacts from "../components/modals/ImportContacts";
import useAuthStore from "../../states/authStore";
import { DataTable, Page } from "@iq/models";
import { FiEye, FiPhone, FiUpload } from "react-icons/fi";
import { IoAdd } from "react-icons/io5";
import AddPhoneCall from "../components/modals/AddPhoneCall";
import { getPhoneCallOutcomeLabel } from "../models/phoneCallOutcomes";

function formatDashboardDateTime(value) {
    if (!value || value === "-" || value === "N/A") {
        return "-";
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return String(value);
    }

    return parsed.toLocaleString();
}

function formatDashboardTouches(value) {
    if (value === null || value === undefined || value === "") {
        return "-";
    }

    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
        return String(value);
    }

    return String(parsed);
}

const CONTACTS_TABLE_CONFIG = {
    columns: [
        { 
            key: "status", 
            label: "Status", 
            accessor: (row) => row?.status || row?.stage || "-", 
            filterable: true, 
            quickValues: ["New", "Aware", "Discovery", "Demo", "Negotiation"],
            width: ".75fr" 
        },
        { 
            key: "name", 
            label: "Name", 
            accessor: (row) => row?.name || `${row?.first_name || ""} ${row?.last_name || ""}`.trim() || "-", 
            filterable: true, 
            quickValues: ["A–E", "F–J", "K–O", "P–T", "U–Z"],
            width: "1fr" 
        },
        { 
            key: "account", 
            label: "Account", 
            accessor: (row) => row?.account || row?.organization_name || row?.company || "-", 
            filterable: true, 
            quickValues: ["A–E", "F–J", "K–O", "P–T", "U–Z"],
            width: "2fr" 
        },
        { 
            key: "email", 
            label: "Email", 
            accessor: (row) => row?.email || "-", 
            filterable: false 
        },
        { 
            key: "phone", 
            label: "Phone", 
            accessor: (row) => row?.phone || row?.phone_number || row?.mobile || "-", 
            filterable: false 
        },
        {
            key: "last_call",
            label: "Last Call",
            accessor: (row) => formatDashboardDateTime(row?.last_call ?? row?.lastCall ?? row?.last_call_at ?? row?.last_call_time),
            filterable: true,
            quickValues: ["Most Recent", "Oldest"],
        },
        {
            key: "last_email",
            label: "Last Email",
            accessor: (row) => formatDashboardDateTime(row?.last_email ?? row?.lastEmail ?? row?.last_email_at ?? row?.last_email_time),
            filterable: true,
            quickValues: ["Most Recent", "Oldest"],
        },
        {
            key: "outcome",
            label: "Outcome",
            accessor: (row) => getPhoneCallOutcomeLabel(row?.outcome ?? row?.last_outcome ?? row?.lastOutcome ?? row?.last_contact_outcome),
            filterable: false,
        },
        {
            key: "touches",
            label: "Touches",
            accessor: (row) => formatDashboardTouches(row?.touches ?? row?.touch_count ?? row?.touchCount ?? row?.touches_count),
            filterable: false,
        },
        {
            key: "next_touch",
            label: "Next Touch",
            accessor: (row) => formatDashboardDateTime(row?.next_touch ?? row?.nextTouch ?? row?.next_touch_at ?? row?.next_step_at),
            filterable: false,
        },
    ],
    actionsColumnWidth: "2fr",
};

function buildContactsRowActions(row, onContactSelect, setLogCallContact) {
    const contact = row?.contact ?? row;

    return [
        {
            id: "view-contact",
            label: "View",
            icon: <FiEye aria-hidden="true" />,
            onClick: () => onContactSelect?.(contact),
        },
        {
            id: "log-call",
            label: "Call",
            icon: <FiPhone aria-hidden="true" />,
            onClick: () => setLogCallContact?.(contact),
        },
    ];
}

function Contacts({
    onContactSelect,
    contacts = [],
    loadingContacts = false,
    onContactsRefresh,
}){
    const [showNewContact, setShowNewContact] = useState(false);
    const [showImportContact, setShowImportContact] = useState(false);
    const [logCallContact, setLogCallContact] = useState(null);

    useAuthStore((state) => state.profile?.name || "");

    function handleCloseNewContact() {
        setShowNewContact(false);
        onContactsRefresh?.();
    }

    function handleImportedContacts() {
        onContactsRefresh?.();
    }

    const headerActions = [
        {
            id: "import-contacts",
            label: "Import Contacts",
            color: "secondary",
            onClick: () => setShowImportContact(true),
            icon: <FiUpload aria-hidden="true" />,
        },
        {
            id: "new-contact",
            label: "New Contact",
            color: "accent",
            onClick: () => setShowNewContact(true),
            icon: <IoAdd aria-hidden="true" />,
        },
    ];

    const contactsRowActions = useMemo(
        () => (row) => buildContactsRowActions(row, onContactSelect, setLogCallContact),
        [onContactSelect]
    );

    return (
        <Page>
            <DataTable
                rowData={contacts} 
                loading={loadingContacts}
                tableConfig={CONTACTS_TABLE_CONFIG}
                headerActions={headerActions}
                onRowClick={(contact) => onContactSelect?.(contact)}
                rowActions={contactsRowActions}
            />

            {showImportContact && (
                <ImportContacts
                    onClose={() => setShowImportContact(false)}
                    onImported={handleImportedContacts}
                />
            )}

            {showNewContact && <NewContact onClose={handleCloseNewContact} />}

            {logCallContact && (
                <AddPhoneCall
                    isOpen={Boolean(logCallContact)}
                    onClose={() => setLogCallContact(null)}
                    deals={[]}
                    contacts={[logCallContact]}
                    onCreated={() => {
                        setLogCallContact(null);
                        onContactsRefresh?.();
                    }}
                />
            )}
        </Page>
    );
}

export default Contacts;